import { useEffect, useState } from 'react';
import { Button, Description, Drawer, Spinner, type OverlayTriggerState } from '@heroui/react';
import { AsyncButton } from '../../../../components/ui/async-button';
import type { Block, BlockFormPayload } from '../../../api/condominiums';
import { DrawerTextField } from '../../../../components/ui/drawer-form-field';
import { FormSwitch } from '../../../../components/ui/form-switch';
import { drawerBodyClassName, drawerFooterClassName, sideDrawerDialogClassName } from '../../../../components/ui/side-drawer-classes';

interface BlockFormDrawerProps {
  state: OverlayTriggerState;
  editing: Block | null;
  onSubmit: (payload: BlockFormPayload) => Promise<void>;
}

const emptyForm: BlockFormPayload = { name: '', code: '', isActive: true };

export function BlockFormDrawer({ state, editing, onSubmit }: BlockFormDrawerProps) {
  const [form, setForm] = useState<BlockFormPayload>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!state.isOpen) return;
    setForm(
      editing
        ? { name: editing.name, code: editing.code ?? '', isActive: editing.isActive }
        : emptyForm,
    );
    setError(null);
    setSubmitting(false);
  }, [state.isOpen, editing]);

  const isValid = form.name.trim().length > 0;

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        name: form.name.trim(),
        code: form.code?.trim() || undefined,
        isActive: form.isActive,
      });
      state.close();
    } catch (err) {
      const message =
        typeof err === 'object' && err !== null && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Não foi possível salvar o bloco.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer state={state}>
      <Drawer.Backdrop isDismissable>
        <Drawer.Content placement="right">
          <Drawer.Dialog className={sideDrawerDialogClassName}>
            <Drawer.Header>
              <Drawer.Heading>{editing ? 'Editar bloco' : 'Novo bloco'}</Drawer.Heading>
              <Drawer.CloseTrigger />
            </Drawer.Header>
            <Drawer.Body className={drawerBodyClassName}>
              <Description>
                {editing
                  ? 'Atualize as informações do bloco.'
                  : 'Organize apartamentos e vagas por bloco ou torre.'}
              </Description>

              <DrawerTextField
                label="Nome do bloco"
                value={form.name}
                placeholder="Ex: Torre A, Bloco 1"
                onChange={(name) => setForm((prev) => ({ ...prev, name }))}
                isDisabled={submitting}
                isInvalid={!!error}
                errorMessage={error ?? undefined}
              />

              <DrawerTextField
                label="Código (opcional)"
                value={form.code ?? ''}
                placeholder="Ex: A, 1, TORRE-A"
                onChange={(code) => setForm((prev) => ({ ...prev, code }))}
                isDisabled={submitting}
                description="Identificador curto do bloco."
              />

              <FormSwitch
                label="Bloco ativo"
                isSelected={form.isActive ?? true}
                onChange={(checked) => setForm((prev) => ({ ...prev, isActive: checked }))}
                isDisabled={submitting}
              />
            </Drawer.Body>
            <Drawer.Footer className={drawerFooterClassName}>
              <Button variant="ghost" isDisabled={submitting} onPress={state.close}>
                Cancelar
              </Button>
              <AsyncButton
                variant="primary"
                isPending={submitting}
                isDisabled={!isValid}
                pendingLabel={editing ? 'Salvar' : 'Criar bloco'}
                onPress={handleSubmit}
              >
                {editing ? 'Salvar' : 'Criar bloco'}
              </AsyncButton>
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
}
